// Arena heading quantisation and land percent (protocol §5.1 ARENA_INPUT, §5.4 arena sections).
//
// Angles are radians, 0 = +x, +y down (screen convention). On the wire a
// heading is `u16` steps of 2π / 65,536 (ARENA_NET.headingSteps). The client quantises BEFORE it
// predicts, so its predictor and the server steer toward exactly the same angle (backlog C-2).
//
// Implemented in the contracts step (lane P owns it).

import { ARENA_NET } from './constants.js';

const TAU = Math.PI * 2;

/** Radians (any value, any sign) → wire steps 0…65,535 (nearest step, wraps at a full turn). */
export function headingToWire(rad: number): number {
  if (!Number.isFinite(rad)) return 0;
  const steps = ARENA_NET.headingSteps;
  // Double modulo: negative angles wrap, and -0 comes out as +0.
  return ((Math.round((rad / TAU) * steps) % steps) + steps) % steps;
}

/** Wire steps → radians in [0, 2π). Out-of-range input wraps. */
export function headingFromWire(steps: number): number {
  const n = ARENA_NET.headingSteps;
  const s = ((Math.trunc(steps) % n) + n) % n;
  return (s / n) * TAU;
}

/** `rad` wrapped into [0, 2π). */
export function wrapAngle(rad: number): number {
  const a = rad % TAU;
  return a < 0 ? a + TAU : a;
}

/**
 * Land percent in hundredths of a percent (`u16` on the wire: 0…10,000), as the HUD, the
 * leaderboard trailer, metrics and `maxLandPct` use it (backlog S-7): land cells / playable cells,
 * rounded. 0 with no playable cells.
 */
export function landPctHundredths(landCells: number, playableCells: number): number {
  if (!(playableCells > 0) || !(landCells > 0)) return 0;
  const v = Math.round((landCells * 10_000) / playableCells);
  return v > 10_000 ? 10_000 : v;
}
